import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, FileSpreadsheet, Info } from "lucide-react";

const download = async (path, params, filename) => {
  const token = localStorage.getItem("khn_token");
  const url = new URL(`${API_BASE}${path}`);
  Object.entries(params).forEach(([k,v]) => v && url.searchParams.set(k, v));
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }});
  const blob = await res.blob();
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
};

export default function LaporanPPhNormal() {
  const [tahun, setTahun] = useState(new Date().getFullYear());
  const [d, setD] = useState({ total_pendapatan:0, total_pengeluaran:0, laba_bersih:0, rincian:[], diskon_50:false, tarif_efektif_persen:11, tarif_dasar_persen:22, total_pph_bayar:0 });

  const load = async () => { const r = await api.get("/laporan/pph-normal", { params: { tahun } }); setD(r.data); };
  useEffect(()=>{ load(); }, [tahun]);

  return (
    <>
      <PageHeader title="Tarif Pajak Normal 22%" subtitle="PPh Badan tarif normal + fasilitas diskon 50% (Pasal 31E) untuk omzet < Rp 4,8 M." actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={()=>download("/export/pph-normal", {fmt:"pdf", tahun}, `pph-normal-${tahun}.pdf`)} className="rounded-sm"><Download size={14} className="mr-1"/> PDF</Button>
          <Button variant="outline" onClick={()=>download("/export/pph-normal", {fmt:"xlsx", tahun}, `pph-normal-${tahun}.xlsx`)} className="rounded-sm"><FileSpreadsheet size={14} className="mr-1"/> Excel</Button>
        </div>
      }/>
      <div className="p-8 space-y-6">
        <div className="card-flat p-5 max-w-xs">
          <Label>Tahun Pajak</Label>
          <Input type="number" value={tahun} onChange={(e)=>setTahun(Number(e.target.value)||tahun)}/>
        </div>
        <div className={`card-flat p-4 flex gap-3 ${d.diskon_50?"border-l-4 border-l-emerald-500":"border-l-4 border-l-amber-500"}`}>
          <Info size={18} className={d.diskon_50?"text-emerald-600":"text-amber-600"}/>
          <div className="text-sm text-slate-700">
            {d.diskon_50
              ? <>Omzet Anda <span className="font-mono">{rupiah(d.total_pendapatan)}</span> di bawah Rp 4.800.000.000 → berhak fasilitas diskon 50%. Tarif efektif: <b>{d.tarif_efektif_persen}%</b>.</>
              : <>Omzet Anda <span className="font-mono">{rupiah(d.total_pendapatan)}</span> ≥ Rp 4.800.000.000 → tarif normal <b>{d.tarif_dasar_persen}%</b> tanpa diskon.</>}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="card-flat p-6" data-testid="pn-pendapatan">
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Total Pendapatan</div>
            <div className="mt-3 font-mono text-2xl text-emerald-700">{rupiah(d.total_pendapatan)}</div>
          </div>
          <div className="card-flat p-6" data-testid="pn-pengeluaran">
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Total Pengeluaran</div>
            <div className="mt-3 font-mono text-2xl text-rose-700">{rupiah(d.total_pengeluaran)}</div>
          </div>
          <div className="card-flat p-6" data-testid="pn-laba">
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Laba Bersih</div>
            <div className="mt-3 font-mono text-2xl text-slate-900">{rupiah(d.laba_bersih)}</div>
          </div>
        </div>
        <div className="card-flat overflow-x-auto">
          <div className="px-5 py-4 border-b border-slate-200 text-sm font-semibold flex justify-between">
            <span>Rincian Bulanan</span>
            <span className="text-slate-500">Tarif efektif: <b className="text-slate-900">{d.tarif_efektif_persen}%</b> {d.diskon_50 && "(22% × 50%)"}</span>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-[12px] uppercase tracking-wide text-slate-500">
              <th className="p-3">Bulan</th>
              <th className="p-3 text-right">Pendapatan</th>
              <th className="p-3 text-right">Pengeluaran</th>
              <th className="p-3 text-right">Laba</th>
              <th className="p-3 text-right">Tarif {d.tarif_efektif_persen}%</th>
              <th className="p-3 text-right">Bayar</th>
            </tr></thead>
            <tbody>
              {d.rincian.length===0 && <tr><td colSpan="6" className="text-center py-10 text-slate-400">Belum ada data.</td></tr>}
              {d.rincian.map(r=>(
                <tr key={r.bulan_key} className="border-b border-slate-100">
                  <td className="p-3">{r.bulan}</td>
                  <td className="p-3 text-right font-mono text-emerald-700">{rupiah(r.pendapatan)}</td>
                  <td className="p-3 text-right font-mono text-rose-700">{rupiah(r.pengeluaran)}</td>
                  <td className="p-3 text-right font-mono">{rupiah(r.laba_bulan)}</td>
                  <td className="p-3 text-right font-mono">{d.tarif_efektif_persen}%</td>
                  <td className="p-3 text-right font-mono font-semibold">{rupiah(r.bayar)}</td>
                </tr>
              ))}
              {d.rincian.length>0 && (
                <tr className="bg-slate-50 font-semibold">
                  <td className="p-3">Total</td>
                  <td className="p-3 text-right font-mono text-emerald-700">{rupiah(d.total_pendapatan)}</td>
                  <td className="p-3 text-right font-mono text-rose-700">{rupiah(d.total_pengeluaran)}</td>
                  <td className="p-3 text-right font-mono">{rupiah(d.laba_bersih)}</td>
                  <td className="p-3 text-right font-mono">-</td>
                  <td className="p-3 text-right font-mono">{rupiah(d.total_pph_bayar)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
